import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, X, Save } from "lucide-react";

function TagEditor({ label, values, onChange, placeholder, testid }) {
  const [input, setInput] = useState("");
  const add = () => { const v = input.trim(); if (v && !values.includes(v)) { onChange([...values, v]); setInput(""); } };
  return (
    <div className="space-y-2" data-testid={testid}>
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        {values.map((v) => (
          <Badge key={v} variant="secondary" className="gap-1 pr-1">
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="hover:text-destructive"><X size={12} /></button>
          </Badge>
        ))}
        {values.length === 0 && <span className="text-xs text-muted-foreground">None yet</span>}
      </div>
      <div className="flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button variant="secondary" size="icon" onClick={add}><Plus size={16} /></Button>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [s, setS] = useState(null);
  useEffect(() => { if (data && !s) setS(data); }, [data]); // eslint-disable-line

  const save = useMutation({
    mutationFn: async () => api.put("/settings", s),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Settings saved"); },
    onError: () => toast.error("Save failed"),
  });

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-muted-foreground" /></div>;
  const set = (k, v) => setS({ ...s, [k]: v });

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">Configure positions, training types, locations, and email recipients.</p>
        </div>
        <Button className="gap-2 h-11 rounded-xl" disabled={save.isPending} onClick={() => save.mutate()} data-testid="save-settings-button">
          {save.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </Button>
      </div>

      <Card className="p-6 space-y-6">
        <TagEditor label="Positions" values={s.positions || []} onChange={(v) => set("positions", v)} placeholder="Add a position…" testid="positions-editor" />
        <TagEditor label="Training Types" values={s.training_types || []} onChange={(v) => set("training_types", v)} placeholder="Add a training type…" testid="types-editor" />
        <TagEditor label="Locations" values={s.locations || []} onChange={(v) => set("locations", v)} placeholder="Add a location…" testid="locations-editor" />
        <TagEditor label="Email Recipients (HR + managers)" values={s.recipient_emails || []} onChange={(v) => set("recipient_emails", v)} placeholder="hr@company.com" testid="recipients-editor" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Email From Name</Label>
            <Input value={s.email_from_name || ""} onChange={(e) => set("email_from_name", e.target.value)} data-testid="from-name-input" />
          </div>
          <div className="space-y-2">
            <Label>Max Upload Size (MB)</Label>
            <Input type="number" value={s.max_upload_mb || 100} onChange={(e) => set("max_upload_mb", parseInt(e.target.value) || 100)} data-testid="max-upload-input" />
          </div>
        </div>
      </Card>
    </div>
  );
}
